import { Box, Typography } from '@mui/material';
import React from 'react';

interface IAuditUser {
    fullname: string;
}

/**
 * The server-stamped audit fields as they arrive from a query.
 *
 * Deliberately NOT part of any form-value interface: those are fed to
 * `SchemaOf<T>` and to initialValues, and these are display-only. Intersect it
 * with the form's value type on the FORM PROP instead —
 * `resource?: (IResource & IAuditStamps) | null`.
 */
export interface IAuditStamps {
    created_by?: IAuditUser | null;
    updated_by?: IAuditUser | null;
}

interface IDAuditInfoProps extends IAuditStamps {
    /**
     * Labels default to the feminine form. Entities whose noun is masculine
     * (recurso, gasto, traspaso, producto...) should pass the masculine one so
     * the text reads correctly.
     */
    createdLabel?: string;
    updatedLabel?: string;
}

/**
 * Read-only "who created / who last touched it" stamps for upsert forms.
 *
 * Display only — nothing here is registered in form state and none of it is
 * ever sent back on the upsert input; the server stamps these from the JWT.
 * Render it only in edit mode: on a new record there is nothing to show.
 * Rows that predate the audit columns have no stamp and render as "—".
 *
 * Pass it to RhFormWrapper's `actionBarStart` prop rather than as a child, so
 * it sits in the sticky action bar opposite the submit button and inherits
 * that bar's padding. No margins of its own — the bar positions it.
 */
export function DAuditInfo({
    created_by,
    updated_by,
    createdLabel = 'Creada por',
    updatedLabel = 'Actualizada por',
}: IDAuditInfoProps) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" component="div">
                {createdLabel}: {created_by?.fullname || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
                {updatedLabel}: {updated_by?.fullname || '—'}
            </Typography>
        </Box>
    );
}
